import React, { useState, useEffect } from "react";
import CaseStudyChart from "@/components/CaseStudyChart";
import SiteLayout from "@/components/SiteLayout";
import IndustriesModule from "@/components/IndustriesModule";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import ServiceBlogSection from "@/components/ServiceBlogSection";
import { VIDEO_REVIEW_URL } from "@/lib/cta";

const ff = "'Barlow', sans-serif";
const cyan = "#57d7e2";
const CDN = "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj";
const LOGO_ASSETS = [
  `/4941-9030.webp`,`/4941-9031.webp`,`/4941-9032.webp`,`/4941-9033.webp`,
  `/4941-9034.webp`,`/4941-9035.webp`,`/4941-9036.webp`,`/4941-9037.webp`,
  `/4941-9038.webp`,`/4941-9039.webp`,`/4941-9040.webp`,`/4941-9041.webp`,
  `/4941-9042.webp`,`/4941-9043.webp`,`/4941-9044.webp`,`/4941-9045.webp`,
  `/4941-9046.webp`,`/4941-9047.webp`,`/4941-9048.webp`,`/4941-9049.webp`,
  `/4941-9050.webp`,`/4941-9051.webp`,`/4941-9052.webp`,`/4941-9053.webp`,
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj/smartfog_87dd78bd.webp",
];
const ctaStyle: React.CSSProperties = { display:"inline-flex",alignItems:"center",gap:"8px",background:"linear-gradient(135deg,#85e2e9 0%,#20b0bc 100%)",border:`1px solid ${cyan}`,borderRadius:"88px",padding:"16px 32px",boxShadow:"0px 2px 15px 0px rgba(87,215,226,0.20)",color:"#121212",fontSize:"16px",fontWeight:600,fontFamily:ff,textDecoration:"none",transition:"opacity 0.2s" };
const microCommit: React.CSSProperties = { display:"block",color:"rgba(255,255,255,0.4)",fontSize:"13px",fontFamily:ff,marginTop:"12px",lineHeight:1.5 };
const H2 = ({ children, mb="64px" }: { children: React.ReactNode; mb?: string }) => (
  <h2 style={{ color:"white",fontSize:"clamp(32px,5vw,48px)",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:`0 0 ${mb}`,textAlign:"center" as const,width:"100%" }}>{children}</h2>
);

const PAIN_POINTS = [
  { n:"01", title:"Your content isn't ranking because it wasn't built to rank", desc:"Most content is written for humans but ignored by Google. Without keyword research, search intent alignment, and proper on-page structure, even excellent writing stays invisible." },
  { n:"02", title:"You're producing content but seeing no pipeline impact", desc:"Publishing blog posts that generate zero leads is a cost, not an investment. Content that doesn't connect to buyer intent and commercial keywords doesn't move revenue." },
  { n:"03", title:"Your competitors are outranking you with inferior content", desc:"If a competitor with weaker writing is ranking above you, it's a structural problem — not a quality problem. They've optimized for search; you haven't." },
  { n:"04", title:"You don't have the capacity to produce content consistently", desc:"SEO content requires volume, consistency, and expertise across research, writing, and optimization. Most teams can't sustain it internally without sacrificing quality or other priorities." },
];

const CASE_STUDIES = [
  { slug: "branding-agency", industry: "Branding & Design Services", metric:"3.5×", metricLabel:"Return on investment", desc:"With a $120K SEO investment over 12 months, a global branding studio generated $1.35M in pipeline and $420K in closed revenue — content was the primary organic growth driver.", before:"$120K SEO investment", after:"$420K revenue, $1.35M pipeline", timeframe:"12 months" },
  { slug: "solar-installer", industry: "Renewable Energy", metric:"$1.1m", metricLabel:"Revenue generated", desc:"Over 10 months, Indexed generated $1.5m in SEO-driven pipeline and $1.1m in revenue for a venture-backed solar group. Content targeting commercial intent queries was central to the strategy.", before:"SEO not tracked as revenue channel", after:"$1.1m revenue, $1.5m pipeline", timeframe:"10 months" },
  { slug: "storage-company", industry: "Storage & Real Estate", metric:"+513%", metricLabel:"Organic click growth", desc:"A national US storage company scaled from 150 to 920 daily organic clicks in 14 months. A structured content program targeting high-intent local and category queries drove the growth.", before:"150 daily organic clicks", after:"920 daily clicks (+513%)", timeframe:"14 months" },
];


const RELATED_SEO_SERVICES = [
  { title:"SEO Audit & Strategy",desc:"Get a complete picture of your SEO health and a prioritized roadmap to grow.",href:"/seo-audit-and-strategy" },
  { title:"Technical SEO",desc:"Fix the infrastructure issues silently suppressing your organic performance.",href:"/technical-seo" },
  { title:"AI SEO",desc:"Stay visible in AI-powered search results and large language model recommendations.",href:"/ai-seo" },
  { title:"Local SEO",desc:"Dominate local search results and drive qualified foot traffic and leads.",href:"/local-seo" },
  { title:"Programmatic SEO",desc:"Capture long-tail demand at scale with data-driven, templated page creation.",href:"/programmatic-seo" },
  { title:"Link Building",desc:"Build domain authority through high-quality editorial backlinks that move rankings.",href:"/link-building" },
];

const FAQS = [
  { q:"Do you sell standalone content writing?", a:"We don't sell isolated content services. Content production is one pillar of the Indexed Full-Stack Search Method™, which covers technical SEO, content, links, and tracking end-to-end. This ensures every piece of content is built to rank, not just to exist." },
  { q:"How do you ensure content actually ranks?", a:"Every piece is built on keyword research, search intent analysis, and competitor gap mapping. We don't write content and hope — we identify exactly what Google wants to rank for a given query and build content that satisfies it." },
  { q:"How much content do you produce per month?", a:"It depends on your competitive landscape and growth targets. We scope content volume based on the keyword opportunities available and what's required to close the gap on competitors. We'll show you the plan on the call." },
  { q:"Can you write for technical or niche industries?", a:"Yes. We've produced content for fintech, biotech, renewable energy, professional services, and SaaS. We have subject matter research processes that allow us to write accurately in complex verticals without requiring you to hand-hold every brief." },
  { q:"How do I know what my content gaps are costing me?", a:"That's exactly what our free video review is for. In a 10–15 minute video recorded for you, we'll show you which keywords your competitors are ranking for that you're not, and what that traffic gap is worth in pipeline. No commitment required." },
];

const NEXT_STEPS = [
  { n:"01", title:"We map your content revenue gap", desc:"In a free personalized video review, we show you exactly which keywords your competitors are capturing that you're missing — and what that gap is worth in pipeline." },
  { n:"02", title:"We show you the content opportunity", desc:"We present a content strategy tied directly to commercial intent — not vanity metrics. You'll see the exact topics, volumes, and revenue potential before we write a single word." },
  { n:"03", title:"We produce, publish, and track", desc:"If it's a fit, we execute. Content production is one pillar of the Indexed Full-Stack Search Method™ — every piece tracked against rankings, traffic, and pipeline." },
];

export default function ContentProduction() {
  useSeoMeta({
    title: "SEO Content Production | Content That Ranks and Converts",
    description: "We create expert-led, search-optimized content that earns rankings and builds authority — from thought leadership to product pages and everything in between.",
    canonical: "/content-production",
    ogType: "website",
    jsonLd: [
    {
      "@type": "Service",
      "serviceType": "Content Production",
      "provider": { "@type": "Organization", "name": "Indexed", "url": "https://www.joinindexed.com" },
      "name": "SEO Content Production",
      "description": "Topic clusters and landing pages built to rank and convert.",
      "url": "https://www.joinindexed.com/content-production"
    }
  ,
  
  {
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do you sell standalone content writing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We don't sell isolated content services. Content production is one pillar of the Indexed Full-Stack Search Method™, which covers technical SEO, content, links, and tracking end-to-end. This ensures every piece of content is built to rank, not just to exist."
        }
      },
      {
        "@type": "Question",
        "name": "How do you ensure content actually ranks?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Every piece is built on keyword research, search intent analysis, and competitor gap mapping. We don't write content and hope — we identify exactly what Google wants to rank for a given query and build content that satisfies it."
        }
      },
      {
        "@type": "Question",
        "name": "How much content do you produce per month?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "It depends on your competitive landscape and growth targets. We scope content volume based on the keyword opportunities available and what's required to close the gap on competitors. We'll show you the plan on the call."
        }
      },
      {
        "@type": "Question",
        "name": "Can you write for technical or niche industries?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. We've produced content for fintech, biotech, renewable energy, professional services, and SaaS. We have subject matter research processes that allow us to write accurately in complex verticals without requiring you to hand-hold every brief."
        }
      },
      {
        "@type": "Question",
        "name": "How do I know what my content gaps are costing me?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "That's exactly what our free video review is for. In a 10–15 minute video recorded for you, we'll show you which keywords your competitors are ranking for that you're not, and what that traffic gap is worth in pipeline. No commitment required."
        }
      }
    ]
  },
  {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.joinindexed.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://www.joinindexed.com/seo-agency"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Content Production",
        "item": "https://www.joinindexed.com/content-production"
      }
    ]
  }],
  });
  const isMobile = useIsMobile();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  return (
    <SiteLayout>

      {/* ── HERO SECTION ── */}
      <div style={{ position: "relative", overflow: "hidden", paddingTop: isMobile ? "60px" : "0" }}>
        {/* Background image */}
        <div style={{
          position: "absolute", top: "-56px", left: "-51px",
          width: "calc(100% + 102px)", height: "calc(100% + 112px)",
          overflow: "hidden", zIndex: 0,
        }}>
          <div style={{ width: "100%", height: "100%", background: "radial-gradient(115% 125% at 78% -10%, #0f3036 0%, #0b1719 38%, #070b0b 72%)" }} />
        </div>
        {/* Cyan glow left */}
        <div style={{ position: "absolute", width: "286px", height: "536px", left: "-80px", top: "-97px", background: "#3aefff", filter: "blur(175px)", opacity: 0.15, transform: "rotate(15deg)", zIndex: 1, pointerEvents: "none" }} />
        {/* Cyan glow center */}
        <div style={{ position: "absolute", width: "474px", height: "474px", left: "473px", top: "-300px", background: "#3aefff", filter: "blur(175px)", opacity: 0.35, transform: "rotate(45deg)", zIndex: 1, pointerEvents: "none" }} />
        {/* Bottom gradient */}
        <div style={{
          position: "absolute", width: "100%", height: "60%", left: 0, bottom: 0,
          background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.92) 80%, rgba(0,0,0,1) 100%)",
          zIndex: 2, pointerEvents: "none",
        }} />
        {/* Left text shadow overlay */}
        <div style={{ position: "absolute", top: 0, left: 0, width: "55%", height: "100%", background: "linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0) 100%)", zIndex: 3, pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 10, display: "flex", flexDirection: "column", paddingTop: "66px" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", padding: isMobile ? "60px 20px 40px" : "60px 72px 40px", maxWidth: "1440px", margin: "0 auto", width: "100%" }}>
            <div style={{ maxWidth: "620px" }}>
              {/* Star rating */}
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                marginBottom: "24px", padding: "6px 14px",
                borderRadius: "100px", border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="#FFD700">
                  <path d="M8 1l1.8 4.6L15 6.2l-3.6 3.5.85 4.95L8 12.3l-4.25 2.35.85-4.95L1 6.2l5.2-.6L8 1z"/>
                </svg>
                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px", fontWeight: 500, fontFamily: ff }}>5.0 from 100+ reviews</span>
              </div>
              {/* Headline */}
              <h1 style={{
                color: "white", fontSize: isMobile ? "36px" : "clamp(40px, 4vw, 60px)", fontWeight: 700,
                lineHeight: 1.08, margin: "0 0 24px", fontFamily: ff, letterSpacing: "-0.5px",
                maxWidth: "560px",
              }}>
                SEO Content that{" "}<span style={{ color: "#57d7e2" }}>ranks and converts.</span>
              </h1>
              {/* Description */}
              <p style={{
                color: "rgba(255,255,255,0.75)", fontSize: "18px", fontWeight: 400,
                lineHeight: 1.6, margin: "0 0 36px", fontFamily: ff, maxWidth: "520px",
              }}>
                Your competitors are ranking for the queries your buyers use. You\'re not.
              </p>
              {/* CTA */}
              <a
                href={VIDEO_REVIEW_URL}
                style={{
                  display: "inline-flex", alignItems: "center", alignSelf: "flex-start", padding: "16px 32px",
                  background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                  borderRadius: "88px", border: "1px solid #57d7e2",
                  boxShadow: "0px 2px 15px 0px rgba(87,215,226,0.25)",
                  color: "#121212", fontSize: "18px", fontWeight: 600,
                  fontFamily: ff, textDecoration: "none",
                  transition: "opacity 0.2s, transform 0.2s",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.9"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
              >
                Get my free video review →
              </a>
            </div>
          </div>
        </div>
      </div>

      <section style={{ background:"#080a0a" }}>
        <div style={{ position:"relative",borderTop:"1px solid rgba(255,255,255,0.08)",padding:"16px 0",overflow:"hidden" }}>
          <div style={{ position:"absolute",inset:0,background:"linear-gradient(to right,#000202 0%,transparent 12%,transparent 88%,#000202 100%)",zIndex:2,pointerEvents:"none" }} />
          <div style={{ display:"flex",alignItems:"center",gap:"50px",animation:"scroll-logos 50s linear infinite",width:"max-content" }}>
            {[...LOGO_ASSETS,...LOGO_ASSETS].map((src,idx)=>(
              <img loading="lazy" key={idx} src={src} alt={`client logo ${idx}`} style={{ width: "135px", height: "90px", objectFit: "contain", flexShrink: 0, filter: "grayscale(1) brightness(2)" }} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-grid" style={{ background:"#0c0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <div>
            <div style={{ textAlign:"center",marginBottom:"48px" }}>
              <H2 mb="24px">The content problems<br /><span style={{ color:cyan }}>quietly costing you revenue.</span></H2>
              <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"16px",fontFamily:ff,lineHeight:1.7,margin:"0 auto",maxWidth:"640px" }}>Every month your content doesn't rank, a competitor captures the leads that should be yours. These gaps compound — and they're fixable.</p>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(2,1fr)",gap:"16px" }}>
              {PAIN_POINTS.map((p,i)=>(
                <div key={i} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:"28px 24px",display:"flex",flexDirection:"column" as const,gap:"12px" }}>
                  <div style={{ color:cyan,fontSize:"11px",fontWeight:700,fontFamily:ff,letterSpacing:"0.1em" }}>{p.n}</div>
                  <h3 style={{ color:"white",fontSize:"17px",fontWeight:700,fontFamily:ff,margin:0,lineHeight:1.3 }}>{p.title}</h3>
                  <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"14px",fontFamily:ff,lineHeight:1.7,margin:0 }}>{p.desc}</p>
                </div>
              ))}
            </div>
            <div style={{ textAlign:"center",marginTop:"40px" }}>
              <a href="/method" style={ctaStyle}>See how it works</a>
            </div>
          </div>
        </div>
      </section>

      <section className="section-grid" style={{ background:"#0a0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <div style={{ textAlign:"center",maxWidth:"760px",margin:"0 auto" }}>
            <H2 mb="20px">Content is the engine<br /><span style={{ color:cyan }}>that compounds over time.</span></H2>
            <p style={{ color:"rgba(255,255,255,0.6)",fontSize:"18px",fontFamily:ff,lineHeight:1.8,margin:0 }}>
              Unlike paid ads, SEO content builds equity. Every piece that ranks generates traffic, leads, and revenue month after month — without additional spend. The businesses that invest in content today own the search results their competitors will be paying to compete with in two years.
            </p>
          </div>
        </div>
      </section>

      <section className="section-grid" style={{ background:"#0c0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <H2 mb="20px">What we cover —<br /><span style={{ color:cyan }}>inside every engagement.</span></H2>
          <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"16px",fontFamily:ff,lineHeight:1.75,margin:"0 auto 48px",maxWidth:"640px",textAlign:"center" as const }}>Content production is one pillar of the Indexed Full-Stack Search Method™. Every piece is built to rank, convert, and compound — not just to fill a content calendar. Not a one-off project. A continuous, measurable engagement.</p>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px" }}>
            {[
              { title:"Keyword research & content mapping", desc:"We identify every keyword your buyers use and map them to content types — from top-of-funnel awareness to bottom-of-funnel commercial intent." },
              { title:"Search intent analysis", desc:"Every piece is built to satisfy the exact intent behind the query — whether informational, commercial, or transactional — so Google ranks it and buyers convert." },
              { title:"Long-form SEO articles & landing pages", desc:"Authoritative, well-structured content that ranks for competitive queries and positions Indexed as the expert in your category." },
              { title:"Content briefs & editorial process", desc:"Every piece goes through a structured brief, research, writing, and optimization process — ensuring consistency, accuracy, and search performance." },
              { title:"Internal linking strategy", desc:"We build a content architecture that passes authority between pages, strengthens your topical depth, and accelerates rankings across your entire site." },
              { title:"Monthly content performance reporting", desc:"Every piece tracked against rankings, traffic, and pipeline — so you always know which content is generating revenue and what to produce next." },
            ].map((item,i)=>(
              <div key={i} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:isMobile ? "28px" : "32px",display:"flex",flexDirection:"column" as const,gap:"14px" }}>
                <div style={{ width:"36px",height:"36px",borderRadius:"50%",background:"rgba(87,215,226,0.08)",border:"1px solid rgba(87,215,226,0.15)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                  <svg width="16" height="16" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke={cyan} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div style={{ color:"white",fontSize:"16px",fontWeight:700,fontFamily:ff,lineHeight:1.3 }}>{item.title}</div>
                <div style={{ color:"rgba(255,255,255,0.5)",fontSize:"14px",fontFamily:ff,lineHeight:1.65 }}>{item.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center",marginTop:"48px" }}>
            <a href="/method" style={ctaStyle}>See how it works</a>
          </div>
        </div>
      </section>

      <section className="section-grid" style={{ background:"#080808",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <div style={{ display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:"16px",marginBottom:"64px" }}>
            <h2 style={{ color:"white",fontSize:"clamp(32px,5vw,48px)",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:0,textAlign:"left" as const }}>Real results,<br /><span style={{ color:cyan }}>real businesses.</span></h2>
            <a href="/client-stories"
              style={{ color: cyan, fontSize: "16px", fontWeight: 600, fontFamily: ff, textDecoration: "none", transition: "opacity 0.2s", flexShrink: 0 }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}>
              See all →
            </a>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px" }}>
            {CASE_STUDIES.map((cs,i)=>(
              <a key={i} href={`/client-stories/${cs.slug}`} style={{ textDecoration: "none", color: "inherit", display: "block" }}>
              <div style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:"32px",display:"flex",flexDirection:"column",gap:"20px" }}>
                <span style={{ color:"rgba(255,255,255,0.4)",fontSize:"11px",fontWeight:700,fontFamily:ff,letterSpacing:"0.1em",textTransform:"uppercase" as const }}>{cs.industry}</span>
                <div>
                  <div style={{ color:cyan,fontSize:"44px",fontWeight:700,fontFamily:ff,lineHeight:1 }}>{cs.metric}</div>
                  <div style={{ color:"rgba(255,255,255,0.5)",fontSize:"13px",fontFamily:ff,marginTop:"6px" }}>{cs.metricLabel}</div>
                </div>
                <CaseStudyChart slug={cs.slug} ariaLabel={`${cs.industry}: ${cs.metric} ${cs.metricLabel.toLowerCase()}`} />
                  <p style={{ color:"rgba(255,255,255,0.7)",fontSize:"14px",fontFamily:ff,lineHeight:1.65,margin:0,flex:1 }}>{cs.desc}</p>
                <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:"20px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px" }}>
                  <div><div style={{ fontSize:"10px",color:"rgba(255,255,255,0.3)",fontFamily:ff,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase" as const,marginBottom:"4px" }}>Before</div><div style={{ fontSize:"13px",fontWeight:600,color:"rgba(255,255,255,0.6)",fontFamily:ff }}>{cs.before}</div></div>
                  <div><div style={{ fontSize:"10px",color:cyan,fontFamily:ff,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase" as const,marginBottom:"4px" }}>After</div><div style={{ fontSize:"13px",fontWeight:600,color:"white",fontFamily:ff }}>{cs.after}</div></div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)", fontFamily: ff }}>Achieved in {cs.timeframe}</span>
                <span style={{ fontSize: "13px", color: cyan, fontFamily: ff, fontWeight: 600 }}>Read case study →</span>
              </div>
              </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="section-grid" style={{ background:"#0a0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <H2 mb="56px">What clients say<br /><span style={{ color:cyan }}>about working with us.</span></H2>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px" }}>
            {[
              { quote:"Exceptional communication throughout the project. The team showed a deep grasp of our goals and delivered a flawless final product that exceeded our expectations.", name:"Flavio", role:"Growth Marketing Director", company:"B2B SaaS" },
              { quote:"Great team to work with, very happy with the consultancy and final documents. They translated complex strategy into something our whole team could act on.", name:"Kirsten", role:"Senior Marketing Manager", company:"Professional Services" },
              { quote:"Indexed turned SEO from a cost center into our highest-ROI acquisition channel. The pipeline numbers speak for themselves — $1.5m in 10 months.", name:"James", role:"CEO", company:"Renewable Energy" },
            ].map((t,i)=>(
              <div key={i} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:"36px",display:"flex",flexDirection:"column",gap:"24px" }}>
                <div style={{ display:"flex",gap:"3px" }}>{[...Array(5)].map((_,s)=><svg key={s} width="14" height="14" viewBox="0 0 12 12" fill="#57d7e2"><path d="M6 1l1.5 3 3.5.5-2.5 2.5.5 3.5L6 9l-3 1.5.5-3.5L1 4.5l3.5-.5z"/></svg>)}</div>
                <p style={{ color:"rgba(255,255,255,0.8)",fontSize:"16px",fontFamily:ff,lineHeight:1.75,margin:0,flex:1,fontStyle:"italic" }}>"{t.quote}"</p>
                <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)",paddingTop:"20px",display:"flex",alignItems:"center",gap:"14px" }}>
                  <div style={{ width:"40px",height:"40px",borderRadius:"50%",background:`linear-gradient(135deg,${cyan}30,${cyan}10)`,border:`1px solid ${cyan}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    <span style={{ color:cyan,fontSize:"15px",fontWeight:700,fontFamily:ff }}>{t.name[0]}</span>
                  </div>
                  <div>
                    <div style={{ color:"white",fontSize:"14px",fontWeight:700,fontFamily:ff }}>{t.name}</div>
                    <div style={{ color:"rgba(255,255,255,0.4)",fontSize:"13px",fontFamily:ff }}>{t.role} · {t.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* RELATED SERVICES */}
      <section className="section-grid" style={{ background:"#0a0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:"24px",marginBottom:"56px" }}>
            <h2 style={{ color:"white",fontSize:"clamp(32px,5vw,48px)",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:0,textAlign:"left" as const }}>More from our<br /><span style={{ color:cyan }}>SEO offering.</span></h2>
            <a href="/seo-agency"
              style={{ color:cyan,fontSize:"16px",fontWeight:600,fontFamily:ff,textDecoration:"none",transition:"opacity 0.2s" }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}>
              View all →
            </a>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px",marginBottom:"18px" }}>
            {RELATED_SEO_SERVICES.slice(0,3).map((svc,i) => (
              <a key={i} href={svc.href} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:"32px",textDecoration:"none",display:"flex",flexDirection:"column",gap:"12px",transition:"border-color 0.2s, transform 0.2s" }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(87,215,226,0.3)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.07)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}>
                <div style={{ width:"28px",height:"2px",background:cyan,borderRadius:"2px" }} />
                <h3 style={{ color:"white",fontSize:"18px",fontWeight:700,fontFamily:ff,margin:0,lineHeight:1.3 }}>{svc.title}</h3>
                <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"14px",fontFamily:ff,lineHeight:1.65,margin:0,flex:1 }}>{svc.desc}</p>
                <span style={{ color:cyan,fontSize:"14px",fontWeight:600,fontFamily:ff }}>Learn more →</span>
              </a>
            ))}
          </div>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px" }}>
            {RELATED_SEO_SERVICES.slice(3).map((svc,i) => (
              <a key={i} href={svc.href} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:"32px",textDecoration:"none",display:"flex",flexDirection:"column",gap:"12px",transition:"border-color 0.2s, transform 0.2s" }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(87,215,226,0.3)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.07)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}>
                <div style={{ width:"28px",height:"2px",background:cyan,borderRadius:"2px" }} />
                <h3 style={{ color:"white",fontSize:"18px",fontWeight:700,fontFamily:ff,margin:0,lineHeight:1.3 }}>{svc.title}</h3>
                <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"14px",fontFamily:ff,lineHeight:1.65,margin:0,flex:1 }}>{svc.desc}</p>
                <span style={{ color:cyan,fontSize:"14px",fontWeight:600,fontFamily:ff }}>Learn more →</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOG ARTICLES ──────────────────────────────────────────── */}
      <ServiceBlogSection categorySlug="content-seo" isMobile={isMobile} />

            {/* ── INDUSTRIES ── */}
      <IndustriesModule isMobile={isMobile} />

      {/* ── FREE REVENUE GAP ANALYSIS CTA ── */}
      <section className="section-grid" style={{ background: "#080a0a", padding: isMobile ? "60px 20px" : "100px 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ maxWidth: "760px", margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "32px" }}>
          {/* Heading */}
          <h2 style={{ color: "white", fontSize: isMobile ? "28px" : "48px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            Find out what your site is losing.<br />{" "}<span style={{ color: "#57d7e2" }}>It's free.</span>
          </h2>
          {/* Body */}
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "18px", fontFamily: ff, lineHeight: 1.7, margin: 0, maxWidth: "520px" }}>
            In a free personalized video review, a top-1% SEO records a walkthrough of your site and shows you exactly what's holding your rankings back — and what fixing it is worth. No pitch. No obligation.
          </p>
          {/* CTA */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
            <a
              href={VIDEO_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: isMobile ? "14px 28px" : "20px 48px",
                background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                borderRadius: "88px",
                border: "1px solid #57d7e2",
                boxShadow: "0px 4px 32px rgba(87,215,226,0.30)",
                textDecoration: "none",
                color: "#0a0c0c",
                fontSize: isMobile ? "15px" : "17px",
                fontWeight: 700,
                fontFamily: ff,
                letterSpacing: "-0.01em",
                transition: "opacity 0.2s, transform 0.2s",
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
            >
              Get my free video review →
            </a>
            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "13px", fontFamily: ff }}>
              No call &middot; No pitch &middot; We take on 3–4 new clients per month — request yours to check availability
            </span>
          </div>
          {/* Stat strip */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", marginTop: "16px", borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "32px", width: "100%", gap: "0" }}>
            {[
              { metric: "5.0 ★", label: "from 100+ reviews" },
              { metric: "100+", label: "engagements delivered" },
              { metric: "Avg. 3.5×", label: "return on SEO investment" },
            ].map((s, i) => (
              <div key={s.metric} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", padding: isMobile ? "12px 8px" : "12px 16px", borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.1)" : "none", textAlign: "center" as const }}>
                <span style={{ color: "#57d7e2", fontSize: isMobile ? "18px" : "22px", fontWeight: 700, fontFamily: ff, lineHeight: 1 }}>{s.metric}</span>
                <span style={{ color: "rgba(255,255,255,0.45)", fontSize: isMobile ? "11px" : "13px", fontFamily: ff, lineHeight: 1.3 }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function FaqItem({ faq, index, openFaq, setOpenFaq, isLast }: { faq:{q:string;a:string}; index:number; openFaq:number|null; setOpenFaq:(i:number|null)=>void; isLast:boolean }) {
  const isOpen = openFaq === index;
  const ff = "'Barlow', sans-serif"; const cyan = "#57d7e2";
  return (
    <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)",borderBottom:isLast?"1px solid rgba(255,255,255,0.08)":"none" }}>
      <button type="button" onClick={()=>setOpenFaq(isOpen?null:index)} style={{ width:"100%",padding:"24px 0",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"16px",background:"transparent",border:"none",cursor:"pointer",textAlign:"left" }}>
        <span style={{ fontSize:"17px",fontWeight:600,color:"white",fontFamily:ff,lineHeight:1.4 }}>{faq.q}</span>
        <span style={{ color:cyan,fontSize:"24px",fontWeight:300,flexShrink:0,transform:isOpen?"rotate(45deg)":"none",transition:"transform 0.2s",lineHeight:1 }}>+</span>
      </button>
      {isOpen&&<div style={{ paddingBottom:"24px" }}><p style={{ margin:0,fontSize:"15px",color:"rgba(255,255,255,0.6)",fontFamily:ff,lineHeight:1.8 }}>{faq.a}</p></div>}
    </div>
  );
}
